import React, { useState, useEffect } from "react";
import { Save, RefreshCw, Award } from "lucide-react";
import Loader from "../../../../components/ui/Loader";
import { Ambassador, AmbassadorUpdatePayload } from "../../../../services/administration/ambassadorService";

export const formatNumber = (n: number | null | undefined, decimals = 2): string => {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatCurrency = (amount: number | null | undefined, currency: string = "XAF"): string => {
  return `${formatNumber(amount)} ${currency}`;
};

interface AmbassadorsTabProps {
  ambassadors: Ambassador[];
  total: number;
  page: number;
  pageSize: number;
  summary: {
    total_ambassadors: number;
    total_filleuls: number;
    total_active_filleuls: number;
    total_revenus_izi: number;
    total_salary: number;
    total_cost: number;
    total_profit_net: number;
  };
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onPageChange: (page: number) => void;
  onUpdate: (userId: string, data: AmbassadorUpdatePayload) => Promise<boolean>;
}

const CURRENCY = "$";

export const AmbassadorsTab: React.FC<AmbassadorsTabProps> = ({
  ambassadors,
  total,
  page,
  pageSize,
  summary,
  loading,
  error,
  onRefresh,
  onPageChange,
  onUpdate,
}) => {
  const [drafts, setDrafts] = useState<Record<string, AmbassadorUpdatePayload>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setDrafts({});
  }, [ambassadors]);

  const updateDraft = (id: string, key: keyof AmbassadorUpdatePayload, value: any) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], [key]: value },
    }));
  };

  const handleSave = async (id: string) => {
    const changes = drafts[id];
    if (!changes) return;
    setSaving((prev) => ({ ...prev, [id]: true }));
    const success = await onUpdate(id, {
      commission_pct: changes.commission_pct,
      commission_amount: changes.commission_amount,
      salary: changes.salary,
      gadgets: changes.gadgets,
      status: changes.status,
      comment: changes.comment,
    });
    if (success) {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
    setSaving((prev) => ({ ...prev, [id]: false }));
  };

  const getValue = (row: Ambassador, key: keyof AmbassadorUpdatePayload, id: string) => {
    const draft = drafts[id]?.[key];
    return draft !== undefined ? draft : (row[key] as any);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 bg-white rounded-xl border space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-800">Tableau de suivi des ambassadeurs ({total})</h2>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg border hover:bg-gray-200 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 bg-green-50 rounded-lg">
          <p className="text-sm text-gray-600">Revenus IZI générés</p>
          <p className="text-xl font-bold text-green-600">
            {formatNumber(summary.total_revenus_izi)} {CURRENCY}
          </p>
        </div>
        <div className="p-4 bg-orange-50 rounded-lg">
          <p className="text-sm text-gray-600">Salaires influenceurs (total versé)</p>
          <p className="text-xl font-bold text-orange-600">
            {formatNumber(summary.total_salary)} {CURRENCY}
          </p>
        </div>
        <div className="p-4 bg-red-50 rounded-lg">
          <p className="text-sm text-gray-600">Coût total ambassadeurs</p>
          <p className="text-xl font-bold text-red-600">
            {formatNumber(summary.total_cost)} {CURRENCY}
          </p>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-gray-600">Profit net généré</p>
          <p className="text-xl font-bold text-blue-600">
            {formatNumber(summary.total_profit_net)} {CURRENCY}
          </p>
        </div>
      </div>

      {loading && ambassadors?.length === 0 ? (
        <Loader />
      ) : ambassadors?.length === 0 ? (
        <p className="text-sm text-gray-400">Aucun ambassadeur trouvé.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-2 py-2 text-left">Ambassadeur</th>
                  <th className="px-2 py-2 text-left">Téléphone</th>
                  <th className="px-2 py-2 text-left">Email</th>
                  <th className="px-2 py-2 text-left">Niveau</th>
                  <th className="px-2 py-2 text-left">Code Promo</th>
                  <th className="px-2 py-2 text-right">Total invités</th>
                  <th className="px-2 py-2 text-right">Total actif</th>
                  <th className="px-2 py-2 text-right">Volume Trans.</th>
                  <th className="px-2 py-2 text-right">Revenus IZI</th>
                  <th className="px-2 py-2 text-right">Bonus Parrain</th>
                  <th className="px-2 py-2 text-right">Bonus Filleul</th>
                  <th className="px-2 py-2 text-right">Comm. %</th>
                  <th className="px-2 py-2 text-right">Comm. $</th>
                  <th className="px-2 py-2 text-right">Salaire (total versé)</th>
                  <th className="px-2 py-2 text-right">Gadgets</th>
                  <th className="px-2 py-2 text-right">Coût total</th>
                  <th className="px-2 py-2 text-right">Profit net</th>
                  <th className="px-2 py-2 text-left">Statut</th>
                  <th className="px-2 py-2 text-left">Commentaire</th>
                  <th className="px-2 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ambassadors.map((a) => {
                  const id = a.id;
                  const hasChanges = !!drafts[id];
                  return (
                    <tr key={id} className="border-b hover:bg-gray-50">
                      <td className="px-2 py-2">{a.ambassador}</td>
                      <td className="px-2 py-2">{a.phone || "—"}</td>
                      <td className="px-2 py-2">{a.email}</td>
                      <td className="px-2 py-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                          {a.level}
                        </span>
                      </td>
                      <td className="px-2 py-2 font-semibold text-green-600">{a.promo_code}</td>
                      <td className="px-2 py-2 text-right">{a.total_invites}</td>
                      <td className="px-2 py-2 text-right">{a.total_active}</td>
                      <td className="px-2 py-2 text-right">{formatNumber(a.volume_transactions)}</td>
                      <td className="px-2 py-2 text-right text-green-600">{formatNumber(a.revenus_izi)}</td>
                      <td className="px-2 py-2 text-right">{formatNumber(a.bonus_parrain)}</td>
                      <td className="px-2 py-2 text-right">{formatNumber(a.bonus_filleul)}</td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={getValue(a, "commission_pct", id) as number}
                          onChange={(e) => updateDraft(id, "commission_pct", parseFloat(e.target.value) || 0)}
                          className="w-16 px-1 py-1 text-right border rounded"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={getValue(a, "commission_amount", id) as number}
                          onChange={(e) => updateDraft(id, "commission_amount", parseFloat(e.target.value) || 0)}
                          className="w-20 px-1 py-1 text-right border rounded"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={getValue(a, "salary", id) as number}
                          onChange={(e) => updateDraft(id, "salary", parseFloat(e.target.value) || 0)}
                          className="w-20 px-1 py-1 text-right border rounded"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={getValue(a, "gadgets", id) as number}
                          onChange={(e) => updateDraft(id, "gadgets", parseFloat(e.target.value) || 0)}
                          className="w-20 px-1 py-1 text-right border rounded"
                        />
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-red-600">{formatNumber(a.total_cost)}</td>
                      <td
                        className={`px-2 py-2 text-right font-semibold ${
                          (a.profit_net || 0) >= 0 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {formatNumber(a.profit_net)}
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={getValue(a, "status", id) as string}
                          onChange={(e) => updateDraft(id, "status", e.target.value)}
                          className="w-20 px-1 py-1 border rounded"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={getValue(a, "comment", id) as string}
                          onChange={(e) => updateDraft(id, "comment", e.target.value)}
                          className="w-32 px-1 py-1 border rounded"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={() => handleSave(id)}
                          disabled={!hasChanges || saving[id]}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium ${
                            hasChanges
                              ? "bg-green-600 text-white hover:bg-green-700"
                              : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          <Save size={12} />
                          {saving[id] ? "..." : "Enreg."}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center pt-2 text-sm text-gray-500">
            <span>
              Affichage {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} sur {total}
            </span>
            <div className="flex gap-2 items-center">
              <button
                disabled={page === 0 || loading}
                onClick={() => onPageChange(page - 1)}
                className="px-3 py-1 rounded-lg border disabled:opacity-40 hover:bg-gray-50"
              >
                Précédent
              </button>
              <span>
                Page {page + 1} / {totalPages}
              </span>
              <button
                disabled={(page + 1) * pageSize >= total || loading}
                onClick={() => onPageChange(page + 1)}
                className="px-3 py-1 rounded-lg border disabled:opacity-40 hover:bg-gray-50"
              >
                Suivant
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AmbassadorsTab;
